import { parseIncompletePhoneNumber } from "libphonenumber-js";
import { createJSONStorage, type PersistStorage } from "zustand/middleware";

import {
  deleteCookie,
  setCookie,
} from "@krea/customer-features/services/cookieService";
import { isRealEstateReason } from "@krea/customer-features/shared-components/loan-application-form/fields/reason";
import { AppName, type CountryCode } from "@krea/shared/types/common";
import { LoanApplicationReasonEnum } from "@krea/shared/types/loanApplication";

import { getUtmDataJSONString } from "../../services/formService";
import { useCustomerFeaturesSettingsStore } from "../app-settings/useCustomerFeaturesSettingsStore";
import {
  type ILoanApplicationFormStore,
  useLoanApplicationFormStore,
} from "./hooks";

const HAS_ONGOING_FILLED_FORM_DATA_COOKIE = "has-ongoing-filled-form-data";
const COOKIE_EXPIRY_DAYS = 7;

export interface IApplicationPayload {
  countryCode: CountryCode;
  requestedLoanAmount: number;
  reason: string;
  customerCompany: {
    organisationNumber: string;
    name: string;
  };
  contactPerson: {
    email: string;
    mobilePhoneNumber: string;
    ssn?: string;
  };
  realEstate?: {
    propertyUnitDesignation: string;
  };
  factoring?: {
    estimatedInvoiceAmount?: number;
    averagePaymentTimeDays: number;
    numberOfInvoices?: number;
    invoicesDescription: string;
  };
  leasing?: {
    leasingAmount: number;
    productType: string;
  };
  utmData?: Record<string, string>;
  remoteIdentificationType?: string;
  isCustomerApplicationFlowAllowed?: boolean;
  notificationReceiver?: string;
}

export const getApplicationPayload = (
  includePropsForInitialization = true,
): IApplicationPayload => {
  const formData = useLoanApplicationFormStore.getState();
  const countryCode = useCustomerFeaturesSettingsStore.getState().countryCode;

  const appPayload: IApplicationPayload = {
    countryCode,
    requestedLoanAmount: formData.requestedLoanAmount,
    reason: formData.reason,
    customerCompany: {
      organisationNumber: formData.organisation.organisationNumber,
      name: formData.organisation.name,
    },
    contactPerson: {
      email: formData.email,
      mobilePhoneNumber: parseIncompletePhoneNumber(formData.mobilePhoneNumber),
    },
  };

  // Add payload specific to the reason/product flow the user took

  const reason = formData.reason as LoanApplicationReasonEnum;

  if (isRealEstateReason(reason)) {
    appPayload.requestedLoanAmount = formData.realEstateRequestedLoanAmount;
    appPayload.realEstate = {
      propertyUnitDesignation: formData.propertyUnitDesignation,
    };
  }

  if (reason === LoanApplicationReasonEnum.INVOICE_FINANCING) {
    const unpaidInvoices = formData.unpaidInvoices;

    appPayload.factoring = {
      estimatedInvoiceAmount: unpaidInvoices.estimatedInvoiceAmount,
      averagePaymentTimeDays: unpaidInvoices.averagePaymentTimeDays,
      numberOfInvoices: unpaidInvoices.numberOfInvoices,
      invoicesDescription: formData.unpaidInvoicesDescription,
    };

    if (appPayload.factoring.estimatedInvoiceAmount) {
      appPayload.requestedLoanAmount =
        appPayload.factoring.estimatedInvoiceAmount;
    }
  }

  if (
    reason === LoanApplicationReasonEnum.OBJECT_FINANCING &&
    formData.leasing.leasingAmount &&
    formData.leasing.leasingObjectType
  ) {
    appPayload.leasing = {
      leasingAmount: formData.leasing.leasingAmount,
      productType: formData.leasing.leasingObjectType,
    };

    appPayload.requestedLoanAmount = appPayload.leasing.leasingAmount;
  }
  const appName = useCustomerFeaturesSettingsStore.getState().appName;

  // Add utm data to payload
  if (appName === AppName.ADMIN && formData.utmData)
    appPayload.utmData = formData.utmData;
  else {
    try {
      const utmDataJSONString = getUtmDataJSONString(formData.utmCampaign);

      if (utmDataJSONString) {
        appPayload.utmData = JSON.parse(utmDataJSONString);
      }
    } catch (e) {
      console.error("Failed to parse utm data", e);
    }
  }

  // == Only for partner initiated applications ==

  // When the application is initiated by a partner/admin, we need to add some extra fields to the payload
  // includePropsForInitialization is used to determine if we should include these fields or not when admin uses this function
  // since admins can both "initiate applications" and "create directly"

  if (
    appName === AppName.PARTNER ||
    (appName === AppName.ADMIN && includePropsForInitialization)
  ) {
    if (formData.ssn) {
      // Add ssn to payload if we are using the "initiate app" feature.
      // If so we, need the ssn from the "Over the phone" BankID part of the flow.
      appPayload.contactPerson.ssn = formData.ssn;
    }

    // Add remoteIdentificationType to payload
    if (formData.remoteIdentification.type) {
      appPayload.remoteIdentificationType = formData.remoteIdentification.type;
    }

    // Add isCustomerApplicationFlowAllowed to payload
    // Used if a partner/admin wants to en/dis-able the customer from ending up inside the application flow.
    appPayload.isCustomerApplicationFlowAllowed =
      formData.remoteIdentification.isCustomerApplicationFlowAllowed;

    // Add notificationReceiver to payload
    // Used to check who should receive the notification
    if (formData.notificationReceiver) {
      appPayload.notificationReceiver = formData.notificationReceiver;
    }
  }

  return appPayload;
};

// We create this cookie to show a banner on websites when there is an ongoing application with some form data filled.
export function syncHasOngoingFilledFormDataCookie(organisation: {
  name: string;
  organisationNumber: string;
}): void {
  const appName = useCustomerFeaturesSettingsStore.getState().appName;
  if (appName !== AppName.CUSTOMER || typeof window === "undefined") return;

  const hasOrganisation = Boolean(
    organisation.name.trim() || organisation.organisationNumber.trim(),
  );

  if (hasOrganisation) {
    setCookie(HAS_ONGOING_FILLED_FORM_DATA_COOKIE, "true", COOKIE_EXPIRY_DAYS);
  } else {
    deleteCookie(HAS_ONGOING_FILLED_FORM_DATA_COOKIE);
  }
}

// Only customer storage should be localStorage, to avoid getting prefilled data from
export function createStorageBasedOnApp():
  | PersistStorage<ILoanApplicationFormStore>
  | undefined {
  const appName = useCustomerFeaturesSettingsStore.getState().appName;

  if (appName === AppName.CUSTOMER) {
    return createJSONStorage(() => localStorage);
  }

  return createJSONStorage(() => sessionStorage);
}
